<template>
  <div>
    <SnackbarComponent
      v-model="snackbar.show"
      :message="snackbar.message"
      :color="snackbar.color"
    />

    <!-- Wider dialog -->
    <v-dialog v-model="dialog" max-width="900px" persistent>
      <v-card>
        <v-card-title class="text-h5 pa-4"> Create Organization </v-card-title>

        <v-divider />

        <!-- Scroll only inside content -->
        <v-card-text class="pa-6" style="max-height: 65vh; overflow-y: auto">
          <v-form ref="form" v-model="valid">
            <!-- ================= Organization Information ================= -->
            <div class="mb-6">
              <h3 class="text-h6 mb-3 primary--text">
                Organization Information
              </h3>

              <v-row dense>
                <v-col cols="6">
                  <v-text-field
                    v-model="formData.organizationName"
                    label="Organization Name*"
                    variant="outlined"
                    density="compact"
                    :rules="[(v) => !!v || 'Organization Name is required']"
                  />
                </v-col>

                <v-col cols="6">
                  <v-text-field
                    v-model="formData.complianceLicense"
                    label="Additional Information"
                    variant="outlined"
                    density="compact"
                  />
                </v-col>

                <v-col cols="12">
                  <v-textarea
                    v-model="formData.registeredAddress"
                    label="Registered Address*"
                    variant="outlined"
                    density="compact"
                    rows="1"
                    auto-grow
                    :rules="[(v) => !!v || 'Registered Address is required']"
                  />
                </v-col>
              </v-row>
            </div>

            <!-- ================= License Section ================= -->
            <div class="mb-6">
              <h3 class="text-h6 mb-3 primary--text">License Configuration</h3>

              <v-radio-group
                v-model="formData.licenseType"
                inline
                class="mb-3"
                label="Plan"
                @update:model-value="bindLicense()"
              >
                <v-radio label="PAID" value="ANNUAL" />
                <v-radio label="TRIAL" value="TRIAL" />
              </v-radio-group>
              <v-radio-group
                v-if="formData.licenseType === 'ANNUAL'"
                v-model="formData.annualSubType"
                inline
                class="mb-3"
                label="Annual Plan Type"
              >
                <v-radio label="Both (Annual + MRR)" value="BOTH" />
                <v-radio label="Annual Only" value="ANNUAL" />
                <v-radio label="MRR Only" value="MRR" />
              </v-radio-group>
              <!-- ANNUAL -->
              <div v-if="formData.licenseType === 'ANNUAL'">
                <v-row dense>
                  <!-- Annual Amount -->
                  <v-col
                    cols="6"
                    v-if="['BOTH', 'ANNUAL'].includes(formData.annualSubType)"
                  >
                    <v-text-field
                      v-model="formData.annualAmount"
                      label="Price Per User Annual"
                      variant="outlined"
                      density="compact"
                      inputmode="numeric"
                      @keydown="allowOnlyNumbers"
                      @paste="preventNonNumericPaste"
                      @input="sanitizeNumber('annualAmount')"
                      :rules="[(v) => !!v || 'Annual price is required']"
                    />
                  </v-col>

                  <!-- No of Users -->
                  <v-col
                    cols="6"
                    v-if="['BOTH', 'ANNUAL'].includes(formData.annualSubType)"
                  >
                    <v-text-field
                      v-model="formData.noOfUsers"
                      label="No of Users"
                      variant="outlined"
                      density="compact"
                      inputmode="numeric"
                      @keydown="allowOnlyNumbers"
                      @paste="preventNonNumericPaste"
                      @input="sanitizeNumber('noOfUsers')"
                      :rules="[(v) => !!v || 'User count is required']"
                    />
                  </v-col>

                  <!-- MRR License -->
                  <v-col
                    cols="6"
                    v-if="['BOTH', 'MRR'].includes(formData.annualSubType)"
                  >
                    <v-text-field
                      v-model="formData.mrrlicense"
                      label="MRR License"
                      variant="outlined"
                      density="compact"
                      inputmode="numeric"
                      @keydown="allowOnlyNumbers"
                      @paste="preventNonNumericPaste"
                      @input="sanitizeNumber('mrrlicense')"
                      :rules="[(v) => !!v || 'MRR license is required']"
                    />
                  </v-col>
                </v-row>
              </div>

              <!-- TRIAL -->
              <div v-if="formData.licenseType === 'TRIAL'">
                <v-row dense>
                  <v-col cols="6">
                    <v-text-field
                      v-model="formData.trialDays"
                      label="Trial Days"
                      variant="outlined"
                      density="compact"
                      type="text"
                      inputmode="numeric"
                      maxlength="5"
                      @keydown="allowOnlyNumbers"
                      @paste="preventNonNumericPaste"
                      @input="sanitizeNumber('trialDays')"
                      :rules="[(v) => !!v || 'Trial days are required']"
                    />
                  </v-col>

                  <v-col cols="6">
                    <v-text-field
                      v-model="formData.noOfUsers"
                      label="No of Users"
                      variant="outlined"
                      density="compact"
                      type="text"
                      inputmode="numeric"
                      maxlength="6"
                      @keydown="allowOnlyNumbers"
                      @paste="preventNonNumericPaste"
                      @input="sanitizeNumber('noOfUsers')"
                      :rules="[(v) => !!v || 'User count is required']"
                    />
                  </v-col>
                </v-row>
              </div>
            </div>

            <!-- ================= Primary Contact ================= -->
            <div class="mb-2">
              <h3 class="text-h6 mb-3 primary--text">
                Primary Contact (Owner / Super Admin)
              </h3>

              <v-row dense>
                <v-col cols="6">
                  <v-text-field
                    v-model="formData.fullName"
                    label="Full Name*"
                    variant="outlined"
                    density="compact"
                    :rules="[(v) => !!v || 'Full Name is required']"
                  />
                </v-col>

                <v-col cols="6">
                  <v-text-field
                    v-model="formData.email"
                    label="Email Address*"
                    type="email"
                    variant="outlined"
                    density="compact"
                    @input="formatEmail"
                    :rules="emailRules"
                  />
                </v-col>

                <v-col cols="6">
                  <v-text-field
                    v-model="formData.mobileNumber"
                    label="Mobile Number"
                    variant="outlined"
                    density="compact"
                    maxlength="12"
                    @input="formatPhoneNumber"
                    :rules="[
                      (v) =>
                        !v ||
                        /^\d{3}-\d{3}-\d{4}$/.test(v) ||
                        'Mobile number must be 10 digits',
                    ]"
                  />
                </v-col>
              </v-row>
            </div>
          </v-form>
        </v-card-text>

        <v-divider />

        <!-- ================= Actions ================= -->
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn variant="text" color="grey" @click="handleClose">
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            variant="flat"
            :loading="loading"
            :disabled="loading"
            @click="handleSubmit"
          >
            Create
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>


<script>
import { generateClient } from "aws-amplify/api";
const client = generateClient();
import GetLicense from "@/mixins/Licensesetting/GetLicense";
import { create_organization } from "@/graphql/mutations";
import SnackbarComponent from "@/components/Extras/Snackbar.vue";
import countryList from "@/JsonFiles/CountryList.json";

export default {
  name: "CreateOrganizationDialog",
  mixins: [GetLicense],
  components: {
    SnackbarComponent,
  },
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:modelValue", "created"],
  data() {
    return {
      valid: false,
      loading: false,
      snackbar: {
        show: false,
        message: "",
        color: "success",
      },
      countries: countryList,
      formData: {
        organizationName: "",
        registeredAddress: "",
        complianceLicense: "",
        fullName: "",
        email: "",
        country: "+1",
        mobileNumber: "",
        licenseType: "ANNUAL",
        annualAmount: "",
        mrrlicense: "",
        trialDays: "",
        noOfUsers: "",
        annualSubType: "BOTH", // BOTH | ANNUAL | MRR
      },
      emailRules: [
        (v) => !!v || "Email is required",
        (v) =>
          /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(
            String(v || "").trim()
          ) || "Email must be valid",
      ],
    };
  },
  computed: {
    dialog: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit("update:modelValue", value);
      },
    },
  },
  watch: {
    async modelValue(newVal) {
      if (newVal) {
        await this.fetchLicense();
        await this.bindLicense();
        // this.setDefaultCountry(); // Commented out - using hardcoded USA
      } else {
        this.resetForm();
      }
    },
    "formData.annualSubType"(val) {
      if (val === "ANNUAL") {
        this.formData.mrrlicense = "";
      }

      if (val === "MRR") {
        this.formData.annualAmount = "";
        this.formData.noOfUsers = "";
      }
    },
  },
  async mounted() {
    // this.setDefaultCountry(); // Commented out - using hardcoded USA
  },
  methods: {
    bindLicense() {
      console.log(this.LicenseList);
      if (!this.LicenseList) return;

      this.formData.annualAmount = this.LicenseList.annual_plan_price || "";
      this.formData.noOfUsers =
        this.formData.licenseType == "ANNUAL"
          ? this.LicenseList.base_user_count || ""
          : this.LicenseList.trial_user_count || "";

      this.formData.trialDays = this.LicenseList.default_trial_days || "";
      console.log(this.formData);
    },
    allowOnlyNumbers(e) {
      const allowedKeys = [
        "Backspace",
        "Delete",
        "ArrowLeft",
        "ArrowRight",
        "Tab",
      ];

      if (allowedKeys.includes(e.key)) return;

      if (!/^\d$/.test(e.key)) {
        e.preventDefault();
      }
    },

    sanitizeNumber(modelKey) {
      this.formData[modelKey] = this.formData[modelKey].replace(/\D/g, "");
    },
    getDefaultCountry() {
      try {
        // Primary method: Detect from timezone
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        // console.log("Browser timezone:", timezone);

        // Comprehensive timezone to country mapping
        const timezoneCountryMap = {
          // India timezones
          "Asia/Kolkata": "IN",
          "Asia/Calcutta": "IN",
          "Asia/Colombo": "IN",
          // US timezones
          "America/New_York": "US",
          "America/Chicago": "US",
          "America/Denver": "US",
          "America/Los_Angeles": "US",
          "America/Phoenix": "US",
          "America/Anchorage": "US",
          "America/Detroit": "US",
          "America/Indianapolis": "US",
          "America/Louisville": "US",
          "America/Miami": "US",
          "America/Montreal": "US",
          "America/Toronto": "US",
          "America/Vancouver": "US",
          // Europe
          "Europe/London": "GB",
          "Europe/Paris": "FR",
          "Europe/Berlin": "DE",
          "Europe/Madrid": "ES",
          "Europe/Rome": "IT",
          "Europe/Amsterdam": "NL",
          "Europe/Brussels": "BE",
          "Europe/Vienna": "AT",
          "Europe/Zurich": "CH",
          // Asia
          "Asia/Dhaka": "BD",
          "Asia/Karachi": "PK",
          "Asia/Tokyo": "JP",
          "Asia/Shanghai": "CN",
          "Asia/Hong_Kong": "HK",
          "Asia/Singapore": "SG",
          "Asia/Bangkok": "TH",
          "Asia/Jakarta": "ID",
          "Asia/Manila": "PH",
          "Asia/Seoul": "KR",
          "Asia/Dubai": "AE",
          "Asia/Riyadh": "SA",
          // Australia
          "Australia/Sydney": "AU",
          "Australia/Melbourne": "AU",
          "Australia/Brisbane": "AU",
          "Australia/Perth": "AU",
          "Australia/Adelaide": "AU",
          // Others
          "Africa/Cairo": "EG",
          "Africa/Johannesburg": "ZA",
          "America/Sao_Paulo": "BR",
          "America/Mexico_City": "MX",
          "America/Buenos_Aires": "AR",
          "America/Lima": "PE",
        };

        // Check exact timezone match first
        const mappedCode = timezoneCountryMap[timezone];
        if (mappedCode) {
          const country = this.countries.find((c) => c.code === mappedCode);
          if (country) {
            // console.log("Found country from timezone:", country);
            return country;
          }
        }

        // Check if timezone contains country-related keywords
        const timezoneLower = timezone.toLowerCase();
        if (
          timezoneLower.includes("kolkata") ||
          timezoneLower.includes("calcutta") ||
          timezoneLower.includes("india")
        ) {
          const country = this.countries.find((c) => c.code === "IN");
          if (country) {
            // console.log("Found India from timezone keyword:", country);
            return country;
          }
        }

        // Fallback: Try to get country from browser locale
        const locale = navigator.language || navigator.userLanguage || "";
        // console.log("Browser locale:", locale);
        const countryCode = locale.split("-")[1] || locale.split("_")[1];
        // console.log("Extracted country code from locale:", countryCode);

        if (countryCode) {
          const country = this.countries.find(
            (c) => c.code === countryCode.toUpperCase()
          );
          if (country) {
            // console.log("Found country from locale:", country);
            return country;
          }
        }
      } catch (error) {
        console.error("Error detecting country:", error);
      }

      // Default to India
      // console.log("Defaulting to India");
      return this.countries.find((c) => c.code === "IN") || this.countries[0];
    },
    setDefaultCountry() {
      if (!this.formData.country) {
        this.formData.country = this.getDefaultCountry();
      }
    },
    formatEmail() {
      // Convert email to lowercase
      this.formData.email = this.formData.email.toLowerCase();
    },
    formatPhoneNumber() {
      // Remove all non-numeric characters
      let value = this.formData.mobileNumber.replace(/\D/g, "");

      // Limit to 10 digits
      if (value.length > 10) {
        value = value.slice(0, 10);
      }

      // Format as xxx-xxx-xxxx
      if (value.length > 6) {
        value =
          value.slice(0, 3) + "-" + value.slice(3, 6) + "-" + value.slice(6);
      } else if (value.length > 3) {
        value = value.slice(0, 3) + "-" + value.slice(3);
      }

      this.formData.mobileNumber = value;
    },
    handleClose() {
      this.dialog = false;
      this.resetForm();
    },
    resetForm() {
      this.formData = {
        organizationName: "",
        registeredAddress: "",
        complianceLicense: "",
        fullName: "",
        email: "",
        country: "+1",
        mobileNumber: "",
        licenseType: "ANNUAL",
        annualSubType: "BOTH",
        mrrlicense: "",
        annualAmount: "",
        trialDays: "",
        noOfUsers: "",
      };

      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },
    getBackendLicenseType() {
    if (this.formData.licenseType === "TRIAL") {
      return "TRIAL";
    }

    // licenseType === ANNUAL
    switch (this.formData.annualSubType) {
      case "ANNUAL":
        return "ANNUAL";
      case "MRR":
        return "MRR";
      case "BOTH":
      default:
        return "HYBRID";
    }
  },
    preventNonNumericPaste(e) {
      const pastedData = (e.clipboardData || window.clipboardData).getData(
        "text"
      );
      if (!/^\d+$/.test(pastedData)) {
        e.preventDefault();
      }
    },

    async handleSubmit() {
      const { valid } = await this.$refs.form.validate();
      if (!valid) {
        return;
      }

      this.loading = true;

      try {
        const input = {
          organization_name: this.formData.organizationName,
          organization_address: this.formData.registeredAddress,
          licence_info: this.formData.complianceLicense,
          owner_name: this.formData.fullName,
          owner_email_id: this.formData.email,
          should_consent_be_visible: true,
          should_summary_page_comments_be_visible:false,
          internal_routing_available_bms:false,
          should_invoice_be_visible:false,
          should_vault_be_visible:false,
          medication_upload_tool_available: false,
          country_code: "+1", // Hardcoded USA dial code
          contact_no: this.formData.mobileNumber,
         license_type: this.getBackendLicenseType(),
          annual_plan_price: ["BOTH", "ANNUAL"].includes(
            this.formData.annualSubType
          )
            ? Number(this.formData.annualAmount)
            : undefined,

          mrr_license_count: ["BOTH", "MRR"].includes(
            this.formData.annualSubType
          )
            ? Number(this.formData.mrrlicense)
            : undefined,
          trial_days:
            this.formData.licenseType === "TRIAL"
              ? Number(this.formData.trialDays)
              : undefined,

          base_user_count: this.formData.noOfUsers
            ? Number(this.formData.noOfUsers)
            : 0,
        };

        const response = await client.graphql({
          query: create_organization,
          variables: {
            input: input,
          },
        });

        console.log(response);
        const responseData = JSON.parse(response.data.create_organization);

        if (responseData.status == "SUCCESS") {
          this.loading = false;
          this.snackbar = {
            show: true,
            message: responseData.status_message,
            color: "success",
          };
          this.$emit("created", 0);
          this.handleClose();
        } else {
          this.loading = false;
          this.snackbar = {
            show: true,
            message: responseData.status_message,
            color: "error",
          };
        }
      } catch (error) {
        this.loading = false;
        console.error("Error creating organization:", error);
        this.snackbar = {
          show: true,
          message:
            error.message ||
            "An error occurred while creating the organization",
          color: "error",
        };
      }
    },
  },
};
</script>

<style scoped>
</style>

